import React, { useEffect, useState } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { apiRequest } from '../api'; // Import the apiRequest function
import ENV_CONFIG from '../config'; // Import the environment config

function ManageGroupCodes() {
  const [currentCode, setCurrentCode] = useState(null);
  const [error, setError] = useState(null);
  const [expandedCode, setExpandedCode] = useState(null);
  const [formData, setFormData] = useState({
    groupName: '',
    code: '',
    capacity: '',
    expiration: '',
    policy: {
      isCFEnable: false,
      isFileUpload: false,
      notification: {
        medium: [],
        schedule: '',
        triggerType: '',
      },
      userDefaultSignup: '',
      voiceAnalysisMode: 'NON_CONTINUOUS_PASSIVE',
      defaultCountryCode: '',
      verifiableSegmentSize: '6',
      showFeatureDescription: false,
    },
  });
  const [groupCodes, setGroupCodes] = useState([]);
  const [invitationData, setInvitationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ show: false, type: '', code: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupNames, setGroupNames] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [addGroupCodeError, setAddGroupCodeError] = useState(null);

  // Get environment from localStorage, default to 'dev'
  const environment = localStorage.getItem('environment') || 'dev';
  const { baseUrl } = ENV_CONFIG[environment];

  useEffect(() => {
    const fetchGroupCodes = async () => {
      try {
        const data = await apiRequest(`${baseUrl}/api/org/code`);
        
        if (data && Array.isArray(data.invitation_data)) {
          // Combine codes and invitation data
          const combinedData = data.invitation_data.map(invitation => ({
            code: invitation.code,
            details: invitation
          }));

          // Sort combined data by code
          combinedData.sort((a, b) => a.code.localeCompare(b.code));

          // Separate sorted data back into codes and invitation data
          const sortedCodes = combinedData.map(item => item.code);
          const sortedInvitationData = combinedData.map(item => item.details);

          setGroupCodes(sortedCodes);
          setInvitationData(sortedInvitationData);
        } else {
          throw new Error('Data does not contain an array of invitation data');
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupCodes();
  }, [baseUrl]);

  const closeModal = () => setModal({ show: false, type: '', code: null });

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleActionClick = (type, code) => {
    setCurrentCode(type === 'Update' ? code : null);
    setModal({ show: true, type, code });
    if (type === 'Update') {
      const invitation = invitationData.find(inv => inv.code === code);
      setFormData({
        groupName: invitation.group_name || '',
        code: invitation.code || '',
        capacity: invitation.capacity || '',
        expiration: invitation.expiration ? formatDateForInput(invitation.expiration) : '',
        policy: {
          isCFEnable: invitation.policy?.isCFEnable || false,
          isFileUpload: invitation.policy?.isFileUpload || false,
                                notification: {
                        medium: invitation.policy?.notification?.medium || [],
                        schedule: invitation.policy?.notification?.schedule || '',
                        triggerType: invitation.policy?.notification?.triggerType || '',
                      },
          userDefaultSignup: invitation.policy?.userDefaultSignup || '',
          voiceAnalysisMode: invitation.policy?.voiceAnalysisMode || 'NON_CONTINUOUS_PASSIVE',
          defaultCountryCode: invitation.policy?.defaultCountryCode || '+1',
          verifiableSegmentSize: invitation.policy?.verifiableSegmentSize || '6',
          showFeatureDescription: invitation.policy?.showFeatureDescription || false,
        },
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (['isCFEnable', 'isFileUpload', 'showFeatureDescription'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        policy: { ...prev.policy, [name]: checked }
      }));
    } else if (['schedule', 'triggerType'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        policy: {
          ...prev.policy,
          notification: { ...prev.policy.notification, [name]: value }
        }
      }));
    } else if (name === 'userDefaultSignup' || name === 'voiceAnalysisMode' || name === 'verifiableSegmentSize' || name === 'defaultCountryCode') {
      setFormData(prev => ({
        ...prev,
        policy: { ...prev.policy, [name]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleMediumChange = (medium) => {
    setFormData(prev => {
      const current = prev.policy.notification.medium;
      return {
        ...prev,
        policy: {
          ...prev.policy,
          notification: {
            ...prev.policy.notification,
            medium: current.includes(medium)
              ? current.filter(m => m !== medium)
              : [...current, medium]
          }
        }
      };
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const isNewCode = currentCode === null;
    const url = isNewCode 
      ? `${baseUrl}/api/org/invitations/${formData.code}`
      : `${baseUrl}/api/org/invitations/${currentCode}`;

    const method = isNewCode ? 'POST' : 'PUT';

    // Create policy object and conditionally exclude empty fields
    const policyData = {
      ...formData.policy,
      verifiableSegmentSize: parseInt(formData.policy.verifiableSegmentSize, 10)
    };
    
    // Remove defaultCountryCode if it's empty or contains only whitespace
    if (!formData.policy.defaultCountryCode || formData.policy.defaultCountryCode.trim() === '') {
      delete policyData.defaultCountryCode;
    }
    
    // Remove userDefaultSignup if it's empty
    if (!formData.policy.userDefaultSignup || formData.policy.userDefaultSignup.trim() === '') {
      delete policyData.userDefaultSignup;
    }
    
    // Handle notification object - remove empty fields
    const notificationData = { ...formData.policy.notification };
    
    // Remove medium if it's an empty array
    if (!notificationData.medium || notificationData.medium.length === 0) {
      delete notificationData.medium;
    }
    
    // Remove schedule if it's empty
    if (!notificationData.schedule || notificationData.schedule.trim() === '') {
      delete notificationData.schedule;
    }
    
    // Remove triggerType if it's empty
    if (!notificationData.triggerType || notificationData.triggerType.trim() === '') {
      delete notificationData.triggerType;
    }
    
    // Update the policy with the cleaned notification object
    policyData.notification = notificationData;

    const body = isNewCode
      ? JSON.stringify({
          group_name: formData.groupName,
          capacity: parseInt(formData.capacity, 10),
          expiration: formData.expiration,
          policy: policyData,
        })
      : JSON.stringify({
          capacity: parseInt(formData.capacity, 10),
          expiration: formData.expiration,
          policy: policyData,
        });

    try {
      const response = await apiRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${JSON.parse(localStorage.getItem('user')).IdToken}`
        },
        body,
      });

      if ('id' in response || 'row_updated_count' in response) {
        closeModal();
        window.location.reload();
      } else {
        throw new Error(`${isNewCode ? 'Failed to add new group code' : 'Failed to update invitation'}`);
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDetails = (code) => {
    setExpandedCode(expandedCode === code ? null : code);
  };

  const handleAddNewGroupCode = async () => {
    setIsFormLoading(true);
    setAddGroupCodeError(null);
    try {
      const data = await apiRequest(`${baseUrl}/api/org/organizations`);
      if (data && Array.isArray(data.organization_data)) {
        // Use a Set to ensure unique group names
        const names = [...new Set(data.organization_data.map(org => org.group_name))];
        setGroupNames(names);
      } else {
        throw new Error('Data does not contain an array of organization data');
      }
      setCurrentCode(null); // Clear current code to indicate a new entry
      setFormData({
        groupName: '',
        code: '',
        capacity: '',
        expiration: '',
        policy: {
          isCFEnable: false,
          isFileUpload: false,
          notification: {
            medium: [], // Initialize medium as an empty array
            schedule: '',
            triggerType: '',
          },
          userDefaultSignup: '',
          voiceAnalysisMode: 'NON_CONTINUOUS_PASSIVE',
          defaultCountryCode: '', // Set to an empty string
          verifiableSegmentSize: '6',
          showFeatureDescription: false,
        },
      });
      setModal({ show: true, type: 'Add', code: null });
    } catch (error) {
      setAddGroupCodeError(error.message); // Set error message
    } finally {
      setIsFormLoading(false); // End loading
    }
  };

  return (
    <Container className="py-4">
      <Card className="shadow">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Manage Group Codes</h4>
          <Button variant="success" onClick={handleAddNewGroupCode}>
            <i className="bi bi-plus-circle me-2"></i>Add New Group Code
          </Button>
        </Card.Header>
        <Card.Body>
          {addGroupCodeError && <Alert variant="danger">{addGroupCodeError}</Alert>}
          {loading ? (
            <div className="d-flex justify-content-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead className="table-light">
                <tr>
                  <th>Code</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {groupCodes.map((code, index) => (
                  <tr key={index}>
                    <td>
                      <Button variant="link" onClick={() => handleActionClick('Details', code)} className="p-0">
                        {code}
                      </Button>
                    </td>
                    <td>
                      <Button variant="info" size="sm" className="me-2" onClick={() => handleActionClick('Details', code)}>
                        <i className="bi bi-list"></i>
                      </Button>
                      <Button variant="primary" size="sm" onClick={() => handleActionClick('Update', code)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal for Add/Update/Details */}
      <Modal show={modal.show} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.type === 'Add' && 'Add New Group Code'}
            {modal.type === 'Update' && `Update Group Code: ${modal.code}`}
            {modal.type === 'Details' && `Details for Group Code: ${modal.code}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(isFormLoading || isSubmitting) ? (
            <div className="d-flex justify-content-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            (() => {
              // Determine if we are in Details mode and get the invitation data
              const isDetails = modal.type === 'Details';
              const invitation = isDetails
                ? invitationData.find(inv => inv.code === modal.code)
                : null;
              // Use invitation data for Details, otherwise use formData
              const data = isDetails && invitation
                ? {
                    groupName: invitation.group_name || '',
                    code: invitation.code || '',
                    capacity: invitation.capacity || '',
                    expiration: invitation.expiration
                      ? formatDateForInput(invitation.expiration)
                      : '',
                    policy: {
                      isCFEnable: invitation.policy?.isCFEnable || false,
                      isFileUpload: invitation.policy?.isFileUpload || false,
                      notification: {
                        medium: invitation.policy?.notification?.medium || [],
                        schedule: invitation.policy?.notification?.schedule || '',
                        triggerType: invitation.policy?.notification?.triggerType || '',
                      },
                      userDefaultSignup: invitation.policy?.userDefaultSignup || '',
                      voiceAnalysisMode: invitation.policy?.voiceAnalysisMode || 'NON_CONTINUOUS_PASSIVE',
                      defaultCountryCode: invitation.policy?.defaultCountryCode || '',
                      verifiableSegmentSize: invitation.policy?.verifiableSegmentSize || '6',
                      showFeatureDescription: invitation.policy?.showFeatureDescription || false,
                    },
                  }
                : formData;

              if (isDetails && !invitation) {
                return <Alert variant="warning">No details found.</Alert>;
              }

              return (
                <Form>
                  {(modal.type === 'Add') && (
                    <Form.Group as={Row} className="mb-3" controlId="formGroupName">
                      <Form.Label column sm={4}>Group Name</Form.Label>
                      <Col sm={8}>
                        <Form.Select name="groupName" value={formData.groupName} onChange={handleInputChange} required>
                          <option value="">Select Group Name</option>
                          {groupNames.map((name, idx) => (
                            <option key={idx} value={name}>{name}</option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  )}
                  {(modal.type === 'Add' || isDetails) && (
                    <Form.Group as={Row} className="mb-3" controlId="formCode">
                      <Form.Label column sm={4}>Code</Form.Label>
                      <Col sm={8}>
                        {isDetails ? (
                          <Form.Control
                            type="text"
                            value={data.code}
                            disabled
                          />
                        ) : (
                          <Form.Control type="text" name="code" value={formData.code} onChange={handleInputChange} required />
                        )}
                      </Col>
                    </Form.Group>
                  )}
                  <Form.Group as={Row} className="mb-3" controlId="formCapacity">
                    <Form.Label column sm={4}>Capacity</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type="number"
                        name="capacity"
                        value={data.capacity}
                        onChange={handleInputChange}
                        required
                        disabled={isDetails}
                        min="0"
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-3" controlId="formExpiration">
                    <Form.Label column sm={4}>Expiration</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type="date"
                        name="expiration"
                        value={data.expiration}
                        onChange={handleInputChange}
                        required
                        disabled={isDetails}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </Col>
                  </Form.Group>
                  <Card className="mb-3">
                    <Card.Header>Policy</Card.Header>
                    <Card.Body>
                      <Row className="mb-2">
                        <Col md={4}>
                          {isDetails ? (
                            <div>
                              <strong>CF: </strong>
                              {data.policy.isCFEnable ? '✔️' : '❌'}
                            </div>
                          ) : (
                            <Form.Check
                              type="checkbox"
                              label="CF"
                              name="isCFEnable"
                              checked={data.policy.isCFEnable}
                              onChange={handleInputChange}
                              disabled={isDetails}
                            />
                          )}
                        </Col>
                        <Col md={4}>
                          {isDetails ? (
                            <div>
                              <strong>File Upload: </strong>
                              {data.policy.isFileUpload ? '✔️' : '❌'}
                            </div>
                          ) : (
                            <Form.Check
                              type="checkbox"
                              label="File Upload"
                              name="isFileUpload"
                              checked={data.policy.isFileUpload}
                              onChange={handleInputChange}
                              disabled={isDetails}
                            />
                          )}
                        </Col>
                        <Col md={4}>
                          {isDetails ? (
                            <div>
                              <strong>Show Feature Description: </strong>
                              {data.policy.showFeatureDescription ? '✔️' : '❌'}
                            </div>
                          ) : (
                            <Form.Check
                              type="checkbox"
                              label="Show Feature Description"
                              name="showFeatureDescription"
                              checked={data.policy.showFeatureDescription}
                              onChange={handleInputChange}
                              disabled={isDetails}
                            />
                          )}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={6}>
                          <Form.Label>Notification Medium</Form.Label>
                          <div>
                            {isDetails ? (
                              <div>
                                <strong>SMS: </strong>
                                {data.policy.notification.medium.includes('SMS') ? '✔️' : '❌'}
                                <span style={{ margin: '0 10px' }}></span>
                                <strong>PN: </strong>
                                {data.policy.notification.medium.includes('PN') ? '✔️' : '❌'}
                              </div>
                            ) : (
                              <>
                                <Form.Check
                                  inline
                                  label="SMS"
                                  type="checkbox"
                                  checked={data.policy.notification.medium.includes('SMS')}
                                  onChange={() => handleMediumChange('SMS')}
                                  disabled={isDetails}
                                />
                                <Form.Check
                                  inline
                                  label="PN"
                                  type="checkbox"
                                  checked={data.policy.notification.medium.includes('PN')}
                                  onChange={() => handleMediumChange('PN')}
                                  disabled={isDetails}
                                />
                              </>
                            )}
                          </div>
                        </Col>
                        <Col md={3}>
                          <Form.Label>Schedule SMS/PN Notification</Form.Label>
                          <Form.Select
                            name="schedule"
                            value={data.policy.notification.schedule}
                            onChange={handleInputChange}
                            disabled={isDetails || data.policy.notification.medium.length === 0}
                          >
                            <option value="">NA</option>
                            <option value="MORNING">MORNING</option>
                            <option value="MIDDAY">MIDDAY</option>
                            <option value="EVENING">EVENING</option>
                            <option value="DAILY">DAILY</option>
                            <option value="WEEKLY">WEEKLY</option>
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label>Trigger Type</Form.Label>
                          <Form.Select
                            name="triggerType"
                            value={data.policy.notification.triggerType}
                            onChange={handleInputChange}
                            disabled={isDetails || data.policy.notification.medium.length === 0}
                          >
                            <option value="">NA</option>
                            <option value="AUTOMATIC">AUTOMATIC</option>
                            <option value="MANUAL">MANUAL</option>
                          </Form.Select>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={6}>
                          <Form.Label>Default Signup</Form.Label>
                          <Form.Select
                            name="userDefaultSignup"
                            value={data.policy.userDefaultSignup}
                            onChange={handleInputChange}
                            disabled={isDetails}
                          >
                            <option value="">NA</option>
                            <option value="PHONE_NUMBER">PHONE_NUMBER</option>
                            <option value="EMAIL">EMAIL</option>
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <Form.Label>Country Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="defaultCountryCode"
                            value={data.policy.defaultCountryCode}
                            onChange={handleInputChange}
                            disabled={isDetails}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Label>Voice Analysis Mode</Form.Label>
                          <Form.Select
                            name="voiceAnalysisMode"
                            value={data.policy.voiceAnalysisMode}
                            onChange={handleInputChange}
                            disabled={isDetails}
                          >
                            <option value="NON_CONTINUOUS_PASSIVE">NON_CONTINUOUS_PASSIVE</option>
                            <option value="CUED">CUED</option>
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <Form.Label>Verifiable Segment Size</Form.Label>
                          <Form.Select
                            name="verifiableSegmentSize"
                            value={data.policy.verifiableSegmentSize}
                            onChange={handleInputChange}
                            disabled={isDetails}
                          >
                            <option value="3">3</option>
                            <option value="6">6</option>
                            <option value="9">9</option>
                          </Form.Select>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Form>
              );
            })()
          )}
        </Modal.Body>
        <Modal.Footer>
          {modal.type !== 'Details' && (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" animation="border" /> : 'Submit'}
            </Button>
          )}
          <Button variant="secondary" onClick={closeModal} disabled={isSubmitting}>
            {modal.type === 'Details' ? 'Close' : 'Cancel'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ManageGroupCodes;